import moment from 'moment';
interface DateOptions {
  doubleBack?: boolean;
  showWithOffset?: boolean;
}

declare global {
  interface Date {
    addHours: (h: number) => Date;
    addMinutes: (m: number) => Date;
    toUTC: (options?: DateOptions) => string | undefined;
  }
}

// `h` can be +ve or -ve
Date.prototype.addHours = function(this: Date, h: number) {
  this.setTime(this.getTime() + h * 60 * 60 * 1000);
  return this;
};

// `m` can be +ve or -ve
Date.prototype.addMinutes = function(this: Date, m: number) {
  this.setTime(this.getTime() + m * 60 * 1000);
  return this;
};

const offset = new Date().getTimezoneOffset();
const offsetString = new Date().toString().match(/([-\+][0-9]+)\s/)?.[1];
Date.prototype.toUTC = function(options) {
  const h = Math.floor(offset / 60);
  const m = offset % 60;

  this.addHours(-h);
  this.addMinutes(-m);

  const formatted = formattedDateTime(this);

  return options?.showWithOffset && offsetString
    ? `${formatted} (${offsetString})`
    : formatted;
};

export const toDate = (date: string | undefined | null): Date | undefined =>
  // https://stackoverflow.com/a/31732581
  date ? new Date(date.replace(/\-/g, '/')) : undefined;

export const formattedDate = (date: string | Date | undefined) => {
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate.format('DD MMM YYYY') : undefined;
};

export const toDigitDateString = (date: string | Date | undefined) => {
  const momentDate = moment(date);
  return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : undefined;
};

export const formattedDateTime = (date: string | Date | undefined) =>
  date ? moment(date).format('DD MMM YYYY, hh:mm A') : date;

export const formattedUTCDateTime = (date: string | undefined) =>
  date ? moment(date).format('DD MMM YYYY, hh:mm A') : date;

export const formatDateForGraphQL = (date: string | Date | undefined) =>
  date ? moment(date, 'DD MMM YYYY').format('YYYY-MM-DD') : date;

// matches date like `2022-06-03`, but not like `2022-06-3`
const findDateRegEx = /\d{4}\-(0?[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])/g;

export const isYearPassed = (year: number | string): boolean => {
  const y = typeof year === 'string' ? Number(year) : year;
  return y < new Date().getFullYear();
};

export const isDatePassed = (date: string | Date | undefined | null) => {
  if (!date) return false;
  if (typeof date === 'string') {
    const d = toDate(date);
    if (!d) return false;
    return d < new Date();
  }
  return date < new Date();
};

export const isPassedMidPoint = (startDate: string, endDate: string) => {
  const start = toDate(startDate);
  const end = toDate(endDate);
  if (!start || !end) return false;
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  if (endMs <= startMs) return false;
  const todayMs = Date.now();
  const midpointMs = startMs + (endMs - startMs) / 2;
  return todayMs > midpointMs && todayMs < endMs;
};

export const replaceDateFormat = (dateString: string | Date) => {
  if (typeof dateString !== 'string') return dateString;
  const dates = dateString.match(findDateRegEx);
  if (dates && dates.length > 0) {
    dates.forEach(d => {
      const value = formattedDate(d);
      dateString = (dateString as string).replace(d, value!);
    });
  }
  return dateString;
};
